import { Outlet, useParams } from "react-router-dom";
import { can, canAny, useAuthStore } from "@/stores/auth-store";
import { AccessDenied } from "@/components/layout/AccessDenied";

/** Gates a route (or subtree, via Outlet) behind one or more permissions. A
 * direct URL visit to a gated route the caller lacks the grant for renders a
 * 403 Access Denied page rather than silently bouncing to the dashboard.
 *
 * `excludeSuperAdmin` closes a gap `can()` otherwise leaves open: it always
 * returns true for a Super Admin regardless of which permission is checked
 * (they hold "*"), so a plain `permission` gate cannot hide a route from
 * them. Per SUPER_ADMIN = PLATFORM_SCOPE_ONLY (see
 * docs/02-authentication-flow.md), every tenant-operational route in this
 * app sets this flag - Employee/Attendance/Payroll/Leave/Reports/Compliance/
 * Expense Management/tenant Audit Log, and also Departments, Holidays,
 * self-service (My Attendance/Payslips/Statutory Info) and tenant
 * Administration (Company, Attendance/Salary Rules, Users & Roles, tenant
 * Billing) - a direct URL visit must 403 the same way the sidebar already
 * hides the item (`NavItem.hiddenFromSuperAdmin`, nav-config.ts). Biometric
 * Integration is the one deliberate exception: its route stays reachable
 * (no excludeSuperAdmin) because TenantWorkspacePage links to it directly
 * once a workspace is open - only the always-on sidebar entry is hidden. */
export function RequirePermission({
  permission,
  any,
  excludeSuperAdmin,
  requireEmployeeLink,
  orSelf,
}: {
  permission?: string;
  any?: string[];
  excludeSuperAdmin?: boolean;
  // Closes a second gap `can()` leaves open, alongside excludeSuperAdmin
  // above: a *.self_read-style permission (compliance.self_read,
  // attendance.self_read, payslip.self_read) is meant for an employee
  // viewing their own record, but Tenant Admin/HR's module-wide wildcard
  // (e.g. "compliance.*") satisfies that same permission string even though
  // neither role is necessarily linked to an Employee record at all - a
  // Tenant Admin account in particular never is. Without this flag such an
  // account passes the permission check, then hits a 403 one layer deeper
  // (ComplianceService.MyStatutoryInfo: "this account is not linked to an
  // employee record") - a confusing dead end reachable straight from the
  // sidebar. Set only on the genuinely self-service routes.
  requireEmployeeLink?: boolean;
  // Also admits a caller who holds `orSelf.permission` (a *.self_read grant)
  // when the route's `orSelf.param` is their OWN linked employee id - the
  // same self-vs-scoped split the backend makes behind
  // RequireAnyPermission(self_read, read). Never admits an unlinked account.
  orSelf?: { permission: string; param: string };
}) {
  const isSuperAdmin = useAuthStore((s) => s.user?.is_super_admin);
  const employeeID = useAuthStore((s) => s.user?.employee_id);
  const params = useParams();
  if (excludeSuperAdmin && isSuperAdmin) return <AccessDenied />;
  if (requireEmployeeLink && !employeeID) return <AccessDenied />;
  const ownRecord = !!orSelf && !!employeeID && params[orSelf.param] === employeeID && can(orSelf.permission);
  const allowed = ownRecord || (permission ? can(permission) : any ? canAny(...any) : true);
  if (!allowed) return <AccessDenied />;
  return <Outlet />;
}

export function RequireSuperAdmin() {
  const user = useAuthStore((s) => s.user);
  if (!user?.is_super_admin) return <AccessDenied />;
  return <Outlet />;
}
